<script lang="ts" setup>
import { BIcon } from "bootstrap-vue";
import { marked } from "marked";
import { computed, ref } from "vue";
import Feedback from "./Feedback.vue";
import Text2Speech from "./Text2Speech.vue";
import WaitingDots from "./WaitingDots.vue";
import { useProtocolStore } from "./store/protocol-store";
import { Exchange } from "./types/Exchange";
import { FeedbackEvent } from "./types/FeedbackEvent";

const protocolStore = useProtocolStore();

const props = defineProps<{
  exchange: Exchange;
  streaming?: boolean;
}>();

const emit = defineEmits<{
  (eventName: "feedback", event: FeedbackEvent): void;
}>();

const handleFeedback = ({ questionUuid, feedbackType, feedbackText }: FeedbackEvent) => {
  emit("feedback", { questionUuid, feedbackType, feedbackText });
};

const shouldShowDetailed = ref(false);

const detailedRequested = ref(false);

const toggleDetailed = () => {
  shouldShowDetailed.value = !shouldShowDetailed.value;

  if (
    shouldShowDetailed.value &&
    !detailedRequested.value &&
    props.exchange.questionUuid &&
    (!props.exchange.detailedResult || props.exchange.detailedResult.length <= 1)
  ) {
    detailedRequested.value = true;
    protocolStore.getDetailedResponse(props.exchange.questionUuid, props.exchange.question || "");
  }
};

const stripMarkdownCodeBlock = (text: string): string => {
  // Remove leading ```markdown or ``` and trailing ```
  return text.replace(/^```(?:markdown)?\n?/, "").replace(/```[\s\n]*$/, ""); // Remove trailing backticks and any whitespace/newlines
};

const markdownToDisplay = computed(() => {
  let raw = shouldShowDetailed.value ? props.exchange.detailedResult ?? "" : props.exchange.summarizedResult ?? "";
  return stripMarkdownCodeBlock(raw);
});

const renderedMarkdown = computed(() => {
  return marked(markdownToDisplay.value);
});

const shouldShowFeedback = () => {
  return protocolStore.shouldShowFeedback.value && !props.streaming;
};

const isDetailedLoading = computed(() => !!props.exchange.detailedLoading);

const uniquePageLinks = computed(() => {
  return Array.from(new Set(props.exchange.pageLinks));
});

const generatingLabel = computed(() => {
  return props.streaming && protocolStore.shouldShowAnswer.value && !props.exchange.question
    ? "Generating summary"
    : undefined;
});

const showWaitingDots = computed(() => {
  if (props.streaming) {
    if (protocolStore.shouldShowAnswer.value) {
      return !markdownToDisplay.value.length;
    } else {
      return !props.exchange.pageLinks?.length;
    }
  }

  if (isDetailedLoading.value && shouldShowDetailed.value) {
    return !markdownToDisplay.value.length;
  }

  return false;
});
</script>

<template>
  <div class="exchange">
    <div v-if="props.exchange.question" class="message-container user-message">
      <div class="message-content">
        <div class="message-text">
          {{ props.exchange.question }}
        </div>
        <div v-if="props.exchange.questionDatetime" class="message-time">
          {{ props.exchange.questionDatetime.toLocaleTimeString() }}
        </div>
      </div>
    </div>

    <div class="message-container system-message">
      <div class="message-content">
        <div class="message-text">
          <div class="result-scroll" :class="{ 'streaming-result': props.streaming || isDetailedLoading }">
            <template v-if="showWaitingDots">
              <WaitingDots :label="generatingLabel" />
            </template>
            <template v-else>
              <div data-id="answer-display" v-html="renderedMarkdown"></div>
            </template>
          </div>
          <button
            v-if="!props.streaming"
            class="btn btn-outline-primary btn-sm mt-3"
            :disabled="isDetailedLoading"
            @click="toggleDetailed"
          >
            <BIcon v-if="isDetailedLoading" icon="arrow-clockwise" animation="spin"></BIcon>
            <BIcon v-else icon="info-circle"></BIcon>
            {{ shouldShowDetailed ? "Hide details" : "Show details" }}
          </button>
        </div>

        <div v-if="props.exchange.answerDatetime" class="message-time">
          {{ props.exchange.answerDatetime.toLocaleTimeString() }}
        </div>
      </div>
    </div>
    <div class="bottom-row">
      <div v-if="props.exchange.pageLinks?.length" class="references">
        <div class="references-list">
          <a
            v-for="reference in uniquePageLinks"
            :key="reference"
            :href="reference"
            target="_blank"
            class="reference-link"
          >
            <BIcon icon="link45deg"></BIcon>
            <span class="reference-number">{{ reference.split("=").at(-1) }}</span>
          </a>
        </div>
      </div>
      <div></div>

      <div v-if="shouldShowFeedback()" class="bottom-row-container">
        <div class="feedback-container">
          <Feedback :exchange="props.exchange" @feedback="handleFeedback" />
        </div>
        <div class="speech-container">
          <Text2Speech :text="markdownToDisplay" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.exchange {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.message-container {
  display: flex;
  width: 100%;
  margin: 0.5rem 0;

  &.user-message {
    justify-content: flex-end;

    .message-content {
      background-color: #007bff;
      color: white;
      border-radius: 1rem 0 1rem 1rem;
      margin-left: 20%;
    }
  }

  &.system-message {
    justify-content: flex-start;

    .message-content {
      background-color: #f0f0f0;
      color: #333;
      border-radius: 0 1rem 1rem 1rem;
      margin-right: 20%;
    }
  }
}

.streaming-result {
  display: flex;
  align-items: flex-end;
}

.streaming-indicator {
  display: inline-block;
  margin-left: 2px;
  height: 1.2em;
}

.blinking-cursor {
  animation: blink 1s step-end infinite;
  font-weight: bold;
}

@keyframes blink {
  from,
  to {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }
}

.message-content {
  padding: 0.75rem 1rem;
  max-width: 85%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  word-break: break-word;
  overflow-wrap: break-word;
}

.message-text {
  width: 100%;
  text-align: left;

  :deep(p) {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul),
  :deep(ol) {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  :deep(code) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-size: 0.9em;
    white-space: pre-wrap;
    word-break: break-word;
  }

  :deep(pre) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    white-space: pre-wrap;
    word-break: break-word;
    max-width: 100%;
  }

  :deep(img) {
    max-width: 100%;
    height: auto;
  }

  :deep(blockquote) {
    border-left: 4px solid rgba(0, 0, 0, 0.1);
    padding-left: 1rem;
    margin-left: 0;
    margin-bottom: 1rem;
    color: rgba(0, 0, 0, 0.7);
  }
}

.user-message .message-text :deep(a) {
  color: #fff;
  text-decoration: underline;
}

.system-message .message-text :deep(a) {
  color: #007bff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
  text-align: right;
  margin-top: 0.25rem;
}

.tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.tab-btn {
  padding: 0.25rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 1rem;
  background: transparent;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
  }
}

.result-scroll {
  overflow-y: auto;
}

.references {
  font-size: 0.875rem;
}

.references-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.references-list {
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
}

.reference-link {
  color: #007bff;
  text-decoration: none;
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
}

.feedback-container {
  padding: 0.25rem;
  border-radius: 0.25rem;
  background-color: #f0f0f0;
  color: #202020;
}

.speech-container {
  border-radius: 0.25rem;
}

.bottom-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bottom-row-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  align-items: center;
  padding: 0.25rem;
}

// Add smooth transitions
.message-container {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
